// src/Dashboard/admin/layout/ScopeFilters.jsx
import { useMemo, useState } from "react";
import { SlidersHorizontal, RefreshCw, ChevronDown, Search } from "lucide-react";
import { SelectField, ComboboxField } from "../components/ui";

/**
 * Advanced ScopeFilters (upgrade of your existing scope selector)
 *
 * What it does:
 * - Top row matches your screenshot: Scope level + Target + Refresh + Collapse
 * - Adds TWO selection modes (inside the same card):
 *   1) Browse: pick Conference -> Station -> District -> Church depending on level
 *   2) Search: filter by entity type + search any entity quickly
 *
 * Parent provides:
 * - hierarchy lists + getters
 * - chain state (selectedConference/station/district/church)
 * - level/targetId
 * - onRefreshHierarchy
 * - onSyncChainFromSelection(type, id) (optional but recommended)
 */
export default function ScopeFilters({
  loading = false,

  // ✅ main scope output
  level,
  setLevel,
  targetId,
  setTargetId,

  // ✅ chain values (browse)
  selectedConference,
  selectedStation,
  selectedDistrict,
  selectedChurch,
  setSelectedConference,
  setSelectedStation,
  setSelectedDistrict,
  setSelectedChurch,

  // sources
  conferences = [],
  stations = [],
  districts = [],
  churches = [],

  // derived (optional; if not passed we derive here)
  availableStations,
  availableDistricts,
  availableChurches,

  // id helpers
  getConferenceId,
  getStationId,
  getDistrictId,
  getChurchId,
  getIsActive,

  // parent links (to build chain labels + auto-sync)
  getStationConferenceId,
  getDistrictStationId,
  getChurchDistrictId,

  // actions
  onRefreshHierarchy,

  // display
  scopeLabel,
}) {
  const [collapsed, setCollapsed] = useState(false);

  // mode within the SAME selector
  const [mode, setMode] = useState("browse"); // "browse" | "search"
  const [searchType, setSearchType] = useState("all"); // all|conference|station|district|church

  const levelOptions = useMemo(
    () => [
      { value: "union", label: "Union (All)" },
      { value: "conference", label: "Conference" },
      { value: "station", label: "Station" },
      { value: "district", label: "District" },
      { value: "church", label: "Church" },
    ],
    []
  );

  // derive lists if parent didn’t pass them
  const derivedAvailableStations = useMemo(() => {
    if (Array.isArray(availableStations)) return availableStations;
    const confId = String(selectedConference || "all");
    if (confId === "all") return [];
    return (stations || []).filter((s) => String(getStationConferenceId?.(s) || "") === confId);
  }, [availableStations, stations, selectedConference, getStationConferenceId]);

  const derivedAvailableDistricts = useMemo(() => {
    if (Array.isArray(availableDistricts)) return availableDistricts;
    const stId = String(selectedStation || "all");
    if (stId === "all") return [];
    return (districts || []).filter((d) => String(getDistrictStationId?.(d) || "") === stId);
  }, [availableDistricts, districts, selectedStation, getDistrictStationId]);

  const derivedAvailableChurches = useMemo(() => {
    if (Array.isArray(availableChurches)) return availableChurches;
    const distId = String(selectedDistrict || "all");
    if (distId === "all") return [];
    return (churches || []).filter((c) => String(getChurchDistrictId?.(c) || "") === distId);
  }, [availableChurches, churches, selectedDistrict, getChurchDistrictId]);

  const conferenceOptions = useMemo(() => {
    return [
      { value: "all", label: "All Conferences" },
      ...(conferences || [])
        .filter((c) => (getIsActive ? getIsActive(c) !== "0" : true))
        .map((c) => ({
          value: String(getConferenceId(c)),
          label: c?.name || String(getConferenceId(c)),
        })),
    ];
  }, [conferences, getConferenceId, getIsActive]);

  const stationOptions = useMemo(() => {
    return [
      { value: "all", label: "All Stations" },
      ...(derivedAvailableStations || [])
        .filter((s) => (getIsActive ? getIsActive(s) !== "0" : true))
        .map((s) => ({
          value: String(getStationId(s)),
          label: s?.name || String(getStationId(s)),
        })),
    ];
  }, [derivedAvailableStations, getStationId, getIsActive]);

  const districtOptions = useMemo(() => {
    return [
      { value: "all", label: "All Districts" },
      ...(derivedAvailableDistricts || [])
        .filter((d) => (getIsActive ? getIsActive(d) !== "0" : true))
        .map((d) => ({
          value: String(getDistrictId(d)),
          label: d?.name || String(getDistrictId(d)),
        })),
    ];
  }, [derivedAvailableDistricts, getDistrictId, getIsActive]);

  const churchChainLabel = (church) => {
    try {
      const distId = String(getChurchDistrictId?.(church) || "");
      const dist = (districts || []).find((d) => String(getDistrictId(d)) === distId);
      const stId = dist ? String(getDistrictStationId?.(dist) || "") : "";
      const st = (stations || []).find((s) => String(getStationId(s)) === stId);
      const confId = st ? String(getStationConferenceId?.(st) || "") : "";

      const confName = (conferences || []).find((c) => String(getConferenceId(c)) === confId)?.name;
      const stName = st?.name;
      const distName = dist?.name;

      return [confName, stName, distName].filter(Boolean).join(" • ");
    } catch {
      return "";
    }
  };

  const churchOptions = useMemo(() => {
    return [
      { value: "all", label: "All Churches" },
      ...(derivedAvailableChurches || [])
        .filter((c) => (getIsActive ? getIsActive(c) !== "0" : true))
        .map((c) => ({
          value: String(getChurchId(c)),
          label: c?.name || String(getChurchId(c)),
          subLabel: churchChainLabel(c) || undefined,
        })),
    ];
  }, [derivedAvailableChurches, getChurchId, getIsActive]); // churchChainLabel depends on closure vars but ok

  // Build Target options (depends on selected level)
  const targetOptions = useMemo(() => {
    if (level === "conference") return [{ value: "", label: "No target (Conference)" }, ...conferenceOptions.filter((x) => x.value !== "all")];
    if (level === "station") return [{ value: "", label: "No target (Station)" }, ...stationOptions.filter((x) => x.value !== "all")];
    if (level === "district") return [{ value: "", label: "No target (District)" }, ...districtOptions.filter((x) => x.value !== "all")];
    if (level === "church") return [{ value: "", label: "No target (Church)" }, ...churchOptions.filter((x) => x.value !== "all")];
    return [{ value: "", label: "No target (Union)" }];
  }, [level, conferenceOptions, stationOptions, districtOptions, churchOptions]);

  // Search options (across entity types)
  const searchOptions = useMemo(() => {
    const addPrefix = (prefix, id, name, subLabel) => ({
      value: `${prefix}:${id}`,
      label: `${prefixLabel(prefix)}: ${name || id}`,
      subLabel,
    });

    const cons = (conferences || [])
      .filter((c) => (getIsActive ? getIsActive(c) !== "0" : true))
      .map((c) => addPrefix("conference", String(getConferenceId(c)), c?.name));

    const sts = (stations || [])
      .filter((s) => (getIsActive ? getIsActive(s) !== "0" : true))
      .map((s) => addPrefix("station", String(getStationId(s)), s?.name));

    const dists = (districts || [])
      .filter((d) => (getIsActive ? getIsActive(d) !== "0" : true))
      .map((d) => addPrefix("district", String(getDistrictId(d)), d?.name));

    const chs = (churches || [])
      .filter((c) => (getIsActive ? getIsActive(c) !== "0" : true))
      .map((c) =>
        addPrefix("church", String(getChurchId(c)), c?.name, churchChainLabel(c) || undefined)
      );

    if (searchType === "conference") return cons;
    if (searchType === "station") return sts;
    if (searchType === "district") return dists;
    if (searchType === "church") return chs;
    return [...cons, ...sts, ...dists, ...chs];
  }, [
    searchType,
    conferences,
    stations,
    districts,
    churches,
    getIsActive,
    getConferenceId,
    getStationId,
    getDistrictId,
    getChurchId,
  ]);

  function resetChain() {
    setSelectedConference?.("all");
    setSelectedStation?.("all");
    setSelectedDistrict?.("all");
    setSelectedChurch?.("all");
  }

  function applyBrowseSelection(nextLevel, pickedId) {
    setLevel(nextLevel);
    setTargetId(pickedId || "");
  }

  function syncChainFromEntity(type, id) {
    // set chain fields so browse stays consistent after search select
    if (!id) return;
    if (type === "conference") {
      setSelectedConference(String(id));
      setSelectedStation("all");
      setSelectedDistrict("all");
      setSelectedChurch("all");
      return;
    }
    if (type === "station") {
      const st = (stations || []).find((s) => String(getStationId(s)) === String(id));
      const confId = st ? String(getStationConferenceId?.(st) || "") : "";
      if (confId) setSelectedConference(confId);
      setSelectedStation(String(id));
      setSelectedDistrict("all");
      setSelectedChurch("all");
      return;
    }
    if (type === "district") {
      const dist = (districts || []).find((d) => String(getDistrictId(d)) === String(id));
      const stId = dist ? String(getDistrictStationId?.(dist) || "") : "";
      const st = stId ? (stations || []).find((s) => String(getStationId(s)) === stId) : null;
      const confId = st ? String(getStationConferenceId?.(st) || "") : "";
      if (confId) setSelectedConference(confId);
      if (stId) setSelectedStation(stId);
      setSelectedDistrict(String(id));
      setSelectedChurch("all");
      return;
    }
    if (type === "church") {
      const ch = (churches || []).find((c) => String(getChurchId(c)) === String(id));
      const distId = ch ? String(getChurchDistrictId?.(ch) || "") : "";
      const dist = distId ? (districts || []).find((d) => String(getDistrictId(d)) === distId) : null;
      const stId = dist ? String(getDistrictStationId?.(dist) || "") : "";
      const st = stId ? (stations || []).find((s) => String(getStationId(s)) === stId) : null;
      const confId = st ? String(getStationConferenceId?.(st) || "") : "";
      if (confId) setSelectedConference(confId);
      if (stId) setSelectedStation(stId);
      if (distId) setSelectedDistrict(distId);
      setSelectedChurch(String(id));
    }
  }

  function onPickSearch(v) {
    if (!v) return;
    const [type, id] = String(v).split(":");
    if (!type || !id) return;

    // 1) set main scope
    setLevel(type);
    setTargetId(id);

    // 2) sync chain so browse stays coherent
    syncChainFromEntity(type, id);
  }

  // When level changes manually, keep target + chain sane
  function onChangeLevel(next) {
    setLevel(next);

    if (next === "union") {
      setTargetId("");
      resetChain();
      return;
    }

    // Clear target, but keep chain so browse works
    setTargetId("");
    if (mode === "search") {
      // search mode can still pick anything
      return;
    }

    // browse: ensure required parents exist
    if (next === "conference") {
      // ok
      return;
    }
    if (next === "station" && selectedConference === "all") setSelectedConference("all");
    if (next === "district" && (selectedConference === "all" || selectedStation === "all")) {
      // do nothing; UI disables deeper selects
    }
    if (next === "church" && (selectedConference === "all" || selectedStation === "all" || selectedDistrict === "all")) {
      // do nothing
    }
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 min-w-0">
        <div className="p-2 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg shrink-0">
          <SlidersHorizontal className="text-orange-600" size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold truncate">Scope</h2>
          <p className="text-xs sm:text-sm text-slate-500 truncate">
            Choose scope level + target (browse or search).
          </p>
        </div>

        <button
          type="button"
          onClick={onRefreshHierarchy}
          className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh hierarchy
        </button>

        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          title={collapsed ? "Expand" : "Collapse"}
        >
          <ChevronDown size={16} className={collapsed ? "rotate-180 transition-transform" : "transition-transform"} />
          {collapsed ? "Expand" : "Collapse"}
        </button>
      </div>

      {/* Top row (matches your screenshot structure) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 min-w-0">
        <SelectField
          label="Scope level"
          value={level}
          onChange={onChangeLevel}
          options={levelOptions}
        />

        <ComboboxField
          label="Target"
          value={targetId || ""}
          onChange={(v) => setTargetId(v)}
          disabled={level === "union"}
          options={targetOptions.map((o) => ({
            value: o.value,
            label: o.label,
            subLabel: o.subLabel,
          }))}
          placeholder={level === "union" ? "No target (Union)" : "Select target…"}
          searchPlaceholder={`Search ${level === "union" ? "target" : level}…`}
          emptyText="No matches."
        />

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={onRefreshHierarchy}
            className="sm:hidden w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh hierarchy
          </button>

          <div className="hidden sm:block w-full">
            <div className="text-xs text-slate-500">
              Current scope: <span className="font-semibold">{scopeLabel || "—"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* collapsed view still shows current scope */}
      <div className="sm:hidden mt-2 text-xs text-slate-500">
        Current scope: <span className="font-semibold">{scopeLabel || "—"}</span>
      </div>

      {collapsed ? null : (
        <>
          {/* Mode toggle (inside same card) */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setMode("browse")}
                className={`px-3 py-2 text-sm font-semibold ${
                  mode === "browse"
                    ? "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"
                    : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200"
                }`}
              >
                Browse
              </button>
              <button
                type="button"
                onClick={() => setMode("search")}
                className={`px-3 py-2 text-sm font-semibold ${
                  mode === "search"
                    ? "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"
                    : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200"
                }`}
              >
                Search
              </button>
            </div>

            <div className="text-xs text-slate-500">
              {mode === "browse"
                ? "Pick via chain (conference → station → district → church)."
                : "Search any entity fast (with filters)."}
            </div>
          </div>

          {/* Browse mode */}
          {mode === "browse" ? (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 min-w-0">
              {/* conference always for non-union */}
              <SelectField
                label="Conference"
                value={selectedConference}
                onChange={(v) => {
                  setSelectedConference(v);
                  setSelectedStation("all");
                  setSelectedDistrict("all");
                  setSelectedChurch("all");

                  if (level === "conference" && v !== "all") applyBrowseSelection("conference", v);
                  if (level !== "conference") setTargetId("");
                }}
                options={conferenceOptions}
                disabled={level === "union"}
              />

              {(level === "station" || level === "district" || level === "church") ? (
                <SelectField
                  label="Station"
                  value={selectedStation}
                  onChange={(v) => {
                    setSelectedStation(v);
                    setSelectedDistrict("all");
                    setSelectedChurch("all");

                    if (level === "station" && v !== "all") applyBrowseSelection("station", v);
                    if (level !== "station") setTargetId("");
                  }}
                  disabled={level === "union" || selectedConference === "all"}
                  options={stationOptions}
                />
              ) : (
                <div className="hidden md:block" />
              )}

              {(level === "district" || level === "church") ? (
                <SelectField
                  label="District"
                  value={selectedDistrict}
                  onChange={(v) => {
                    setSelectedDistrict(v);
                    setSelectedChurch("all");

                    if (level === "district" && v !== "all") applyBrowseSelection("district", v);
                    if (level !== "district") setTargetId("");
                  }}
                  disabled={level === "union" || selectedStation === "all"}
                  options={districtOptions}
                />
              ) : (
                <div className="hidden md:block" />
              )}

              {level === "church" ? (
                <ComboboxField
                  label="Church"
                  value={selectedChurch}
                  onChange={(v) => {
                    setSelectedChurch(v);
                    if (v !== "all") applyBrowseSelection("church", v);
                  }}
                  disabled={level === "union" || selectedDistrict === "all"}
                  options={churchOptions}
                  placeholder="All Churches"
                  searchPlaceholder="Search church…"
                  emptyText="No churches found."
                />
              ) : (
                <div className="hidden md:block" />
              )}
            </div>
          ) : (
            /* Search mode */
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 min-w-0">
              <SelectField
                label="Filter"
                value={searchType}
                onChange={setSearchType}
                options={[
                  { value: "all", label: "All entities" },
                  { value: "conference", label: "Conferences" },
                  { value: "station", label: "Stations" },
                  { value: "district", label: "Districts" },
                  { value: "church", label: "Churches" },
                ]}
              />

              <ComboboxField
                label="Search target"
                value=""
                onChange={onPickSearch}
                options={searchOptions}
                placeholder="Type to search…"
                searchPlaceholder="Search…"
                emptyText="No matches."
                leftIcon={Search}
              />

              <div className="text-xs text-slate-500 flex items-end">
                Tip: choose “Districts”, type the name, click → scope auto-updates.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function prefixLabel(prefix) {
  if (prefix === "conference") return "Conference";
  if (prefix === "station") return "Station";
  if (prefix === "district") return "District";
  if (prefix === "church") return "Church";
  return "Target";
}
